// Handshake postMessage com o Gestão — lado do IFRAME, contrato v1
// (docs/HANDSHAKE-GESTAO-REMARKETING.md). O Gestão é quem inicia (`init`); o
// iframe pede a credencial (`auth.request`) e só aceita a resposta da
// solicitação pendente, uma vez, dentro do prazo.
//
// Antes de olhar o conteúdo de qualquer mensagem: a origem precisa estar na
// lista do build e a janela precisa ser a mãe. Depois do `init`, só a origem que
// o enviou conversa com a área. Toda mensagem sai com targetOrigin exato — nunca
// '*'. O token não é guardado aqui: quem chama recebe e decide.

export const PROTOCOL_VERSION = 1;

export const MESSAGE = {
  INIT: 'ei.remarketing.init',
  AUTH_REQUEST: 'ei.remarketing.auth.request',
  AUTH_SUCCESS: 'ei.remarketing.auth.success',
  AUTH_ERROR: 'ei.remarketing.auth.error',
} as const;

/** Prazo máximo do token no contrato v1 (o Gestão emite 300 s). */
export const MAX_TOKEN_TTL_SECONDS = 300;
/** O Gestão tem 10 s para buscar o token; a espera daqui cobre isso com folga. */
const DEFAULT_REQUEST_TIMEOUT_MS = 15_000;
const MAX_TOKEN_LENGTH = 4096;
const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

export type HandshakeErrorCode = 'session_expired' | 'forbidden' | 'unavailable' | 'busy' | 'timeout';
const PARENT_ERROR_CODES: ReadonlySet<string> = new Set(['session_expired', 'forbidden', 'unavailable', 'busy']);

export class HandshakeError extends Error {
  readonly code: HandshakeErrorCode;
  constructor(code: HandshakeErrorCode) {
    super(`handshake: ${code}`);
    this.name = 'HandshakeError';
    this.code = code;
  }
}

export interface TokenGrant {
  accessToken: string;
  /** Segundos de validade, contados a partir do recebimento. */
  expiresIn: number;
}

export interface IncomingMessage {
  source: unknown;
  origin: string;
  data: unknown;
}

export interface MessageSource {
  addEventListener(type: 'message', listener: (event: IncomingMessage) => void): void;
  removeEventListener(type: 'message', listener: (event: IncomingMessage) => void): void;
}

export interface ParentWindow {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface HandshakeOptions {
  /** A janela do iframe (onde as mensagens chegam). */
  self: MessageSource;
  /** `window.parent`: a única janela com quem a área conversa. */
  parent: ParentWindow;
  allowedOrigins: readonly string[];
  /** Chamado no primeiro `init` válido. */
  onParentReady: () => void;
  randomId?: () => string;
  requestTimeoutMs?: number;
}

export interface Handshake {
  /** Origem exata da mãe, conhecida a partir do primeiro `init` válido. */
  readonly parentOrigin: string | null;
  requestToken(): Promise<TokenGrant>;
  dispose(): void;
}

function hasExactKeys(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function isValidGrant(data: Record<string, unknown>): boolean {
  const { accessToken, expiresIn } = data;
  return (
    typeof accessToken === 'string' &&
    accessToken.length <= MAX_TOKEN_LENGTH &&
    JWT_SHAPE.test(accessToken) &&
    typeof expiresIn === 'number' &&
    Number.isInteger(expiresIn) &&
    expiresIn > 0 &&
    expiresIn <= MAX_TOKEN_TTL_SECONDS
  );
}

interface PendingRequest {
  id: string;
  promise: Promise<TokenGrant>;
  resolve: (grant: TokenGrant) => void;
  reject: (error: HandshakeError) => void;
  timer: ReturnType<typeof setTimeout>;
}

export function createHandshake(options: HandshakeOptions): Handshake {
  const {
    self,
    parent,
    allowedOrigins,
    onParentReady,
    randomId = () => crypto.randomUUID(),
    requestTimeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
  } = options;

  let parentOrigin: string | null = null;
  let pending: PendingRequest | null = null;
  let disposed = false;

  function settle(outcome: { grant: TokenGrant } | { error: HandshakeError }) {
    const request = pending;
    if (!request) return;
    pending = null;
    clearTimeout(request.timer);
    if ('grant' in outcome) request.resolve(outcome.grant);
    else request.reject(outcome.error);
  }

  function onMessage(event: IncomingMessage) {
    if (disposed || event.source !== parent) return;
    if (!allowedOrigins.includes(event.origin)) return;
    if (parentOrigin !== null && event.origin !== parentOrigin) return;

    const data = event.data;
    if (hasExactKeys(data, ['type', 'version'])) {
      if (data.type !== MESSAGE.INIT || data.version !== PROTOCOL_VERSION) return;
      // Reenvios do init (o Gestão repete até a primeira solicitação) não
      // trocam a mãe nem reabrem nada.
      if (parentOrigin !== null) return;
      parentOrigin = event.origin;
      onParentReady();
      return;
    }

    if (!pending || data === null || typeof data !== 'object') return;
    const envelope = data as Record<string, unknown>;
    if (envelope.version !== PROTOCOL_VERSION || envelope.requestId !== pending.id) return;

    if (envelope.type === MESSAGE.AUTH_SUCCESS) {
      if (
        hasExactKeys(envelope, ['type', 'version', 'requestId', 'accessToken', 'expiresIn']) &&
        isValidGrant(envelope)
      ) {
        settle({
          grant: { accessToken: envelope.accessToken as string, expiresIn: envelope.expiresIn as number },
        });
      } else {
        settle({ error: new HandshakeError('unavailable') });
      }
      return;
    }

    if (envelope.type === MESSAGE.AUTH_ERROR && hasExactKeys(envelope, ['type', 'version', 'requestId', 'code'])) {
      const code = typeof envelope.code === 'string' && PARENT_ERROR_CODES.has(envelope.code)
        ? (envelope.code as HandshakeErrorCode)
        : 'unavailable';
      settle({ error: new HandshakeError(code) });
    }
  }

  self.addEventListener('message', onMessage);

  return {
    get parentOrigin() {
      return parentOrigin;
    },

    requestToken() {
      if (disposed || parentOrigin === null) {
        return Promise.reject(new HandshakeError('unavailable'));
      }
      if (pending) return pending.promise;

      const id = randomId();
      let resolve!: (grant: TokenGrant) => void;
      let reject!: (error: HandshakeError) => void;
      const promise = new Promise<TokenGrant>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      const timer = setTimeout(() => settle({ error: new HandshakeError('timeout') }), requestTimeoutMs);
      pending = { id, promise, resolve, reject, timer };

      parent.postMessage({ type: MESSAGE.AUTH_REQUEST, version: PROTOCOL_VERSION, requestId: id }, parentOrigin);
      return promise;
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      self.removeEventListener('message', onMessage);
      settle({ error: new HandshakeError('unavailable') });
    },
  };
}
